import React, { useState } from 'react';
import { ATMButton, ATMDatePicker, ATMDropdown, ATMGrid, ATMHeader, ATMLoader, IORGDataTableQueryState } from 'shared-it-appmod-ui';
import { ITableFilter } from 'shared-it-appmod-ui/dist/js/components/organisms/ORGDataTable/MOLTable-filter/MOLTable-filter.component';
import { COMPANY_FILTER, TARStatus } from 'src/constants';
import { getIsAdmin } from 'src/libraries/access.library';
import Lang from 'src/libraries/language';
import { IRequestFilters } from 'src/models/request.model';
import TableSearch from 'src/components/atoms/table/table-search/table-search.component';
import filterStyle from 'src/components/pages/flight/flight-request-list/flight-request-list.module.scss';
import style from '../flights-calendar.module.scss';

type IProps = {
  total: number;
  loading: boolean;
  downloading: boolean;
  filters: IRequestFilters;
  parameters: IORGDataTableQueryState;
  handleFetch: () => void;
  handleClear: (data: boolean) => void;
  handleParams: (params: IORGDataTableQueryState) => void;
  handleDownload: (params: IORGDataTableQueryState) => void;
};

const FlightsCalendarFilter: React.FC<IProps> = ({
  total,
  loading,
  filters,
  parameters,
  downloading,
  handleFetch,
  handleClear,
  handleParams,
  handleDownload,
}) => {
  const [filterOn, setFilterOn] = useState(true);
  const [filterParams, setFilterParams] = useState<Record<any, any>[]>([]);

  const setFilterValues = (value, name) => {
    const filter = filterParams.filter((item) => item.name !== name);
    if (value && value.length > 0) {
      filter.push({ name, value });
    }
    setFilterParams(filter);
    handleParams({
      ...parameters,
      page: 1,
      filters: filter as ITableFilter[],
    });
  };

  const getValue = (name) => filterParams.filter((item) => item.name === name).length > 0 ?
    filterParams.filter((item) => item.name === name)[0].value :
    [];

  return (
    <>
      <ATMGrid>
        <ATMGrid.Row columns={2}>
          <ATMGrid.Column width={4}>
            {loading ? (
              <span className={style.recordCountLabel}>
                <ATMLoader active inline size="tiny" />
              </span>
            ) : (
              <span className={style.recordCountLabel}>
                {total > 0 ? (
                  Lang.formatString(
                    Lang.MSG_RECORDS_COUNT,
                    total
                  )
                ) : (Lang.MSG_NO_RECORDS_COUNT)}
              </span>
            )}
          </ATMGrid.Column>
          <ATMGrid.Column
            className={style.filterSection}
            width={12}
          >
            <span>
              {getIsAdmin() ? (
                <TableSearch
                  dropdown
                  state={parameters}
                  setState={handleParams}
                  setClear={handleClear}
                />
              ) : undefined}
            </span>
            <span>
              <ATMDropdown
                selection
                defaultValue={COMPANY_FILTER[0].value}
                options={COMPANY_FILTER}
                onChange={(_, { value }) => {
                  if (value !== COMPANY_FILTER[0].value) {
                    handleParams({
                      ...parameters,
                      page: 1,
                      company: value,
                    });
                  } else {
                    handleParams({
                      ...parameters,
                      page: 1,
                      company: undefined,
                    });
                  }
                  handleFetch();
                }}
              />
            </span>
            <span>
              {
                getIsAdmin() ? (
                  <ATMButton
                    basic
                    color="blue"
                    icon="download"
                    content={Lang.LBL_DOWLOAD}
                    loading={downloading}
                    disabled={downloading || total === 0}
                    onClick={() => handleDownload(parameters)}
                  />
                ) : undefined
              }
            </span>
            <span>
              <ATMButton
                basic
                color="blue"
                icon="filter"
                content={Lang.LBL_FILTERS}
                onClick={() => setFilterOn(!filterOn)}
              />
            </span>
          </ATMGrid.Column>
        </ATMGrid.Row>
        {filterOn && (
          <ATMGrid stretched doubling stackable>
            <ATMGrid.Row columns="equal" className={style.pointBorder}>
              <ATMGrid.Column>
                <ATMHeader
                  as="h3"
                  content={Lang.LBL_DISTRICT_DEPT_PROJECT}
                />
                <ATMDropdown
                  selection
                  multiple
                  clearable
                  search
                  options={filters.districts?.map((dist) => ({
                    key: dist?.replace(' ', ''),
                    value: dist,
                    text: dist,
                  }))}
                  value={getValue('requestorDistrict')}
                  onChange={(_, { value }) => setFilterValues(value, 'requestorDistrict')}
                />
              </ATMGrid.Column>
              <ATMGrid.Column>
                <ATMHeader as="h3" content={Lang.LBL_STATUS} />
                <ATMDropdown
                  selection
                  multiple
                  clearable
                  search
                  options={filters.requestStatuses?.map((status) => ({
                    key: status?.replace(' ', ''),
                    value: status,
                    text: status,
                  }))}
                  value={getValue('requestStatus')}
                  onChange={(_, { value }) => setFilterValues(value, 'requestStatus')}
                />
              </ATMGrid.Column>
              <ATMGrid.Column>
                <ATMHeader as="h3" content={Lang.LBL_FLIGHT_TYPE} />
                <ATMDropdown
                  selection
                  multiple
                  clearable
                  search
                  options={filters.flightTypes?.map((type) => ({
                    key: type?.replace(' ', ''),
                    value: type,
                    text: type,
                  }))}
                  value={getValue('flightType')}
                  onChange={(_, { value }) => setFilterValues(value, 'flightType')}
                />
              </ATMGrid.Column>
              <ATMGrid.Column>
                <ATMHeader as="h3" content={Lang.LBL_OPERATOR} />
                <ATMDropdown
                  selection
                  multiple
                  clearable
                  search
                  options={filters.operatorNames?.map((operator) => ({
                    key: operator?.replace(' ', ''),
                    value: operator,
                    text: operator,
                  }))}
                  value={getValue('operatorName')}
                  onChange={(_, { value }) => setFilterValues(value, 'operatorName')}
                />
              </ATMGrid.Column>
            </ATMGrid.Row>
            <ATMGrid.Row columns="equal" className={style.filterRow}>
              <ATMGrid.Column>
                <ATMHeader as="h3" content={Lang.LBL_TAIL_NO} />
                <ATMDropdown
                  selection
                  clearable
                  search
                  multiple
                  options={filters.tailNumbers?.map((tail) => ({
                    key: tail?.replace(' ', ''),
                    value: tail,
                    text: tail,
                  }))}
                  value={getValue('tailNumber')}
                  onChange={(_, { value }) => setFilterValues(value, 'tailNumber')}
                />
              </ATMGrid.Column>
              <ATMGrid.Column className={filterStyle.datepicker}>
                <ATMHeader
                  as="h3"
                  content={Lang.LBL_FLIGHT_DATE_RANGE}
                />
                <ATMDatePicker
                  type="range"
                  format="MM/DD/YYYY"
                  placeholder={Lang.LBL_SELECT}
                  value={getValue('flightDate')}
                  onChange={(_, { value }) => setFilterValues(value, 'flightDate')}
                />
              </ATMGrid.Column>
              <ATMGrid.Column>
                <ATMHeader as="h3" content={Lang.LBL_TAR_STATUS} />
                <ATMDropdown
                  selection
                  multiple
                  clearable
                  search
                  options={filters.tarStatuses?.map((tar) => ({
                      key: tar?.replace(' ', ''),
                      value: tar,
                      text: TARStatus[Number(tar)],
                  }))}
                  value={getValue('tarStatus')}
                  onChange={(_, { value }) => setFilterValues(value, 'tarStatus')}
                />
              </ATMGrid.Column>
              <ATMGrid.Column>
                <ATMHeader as="h3" content={Lang.LBL_REQUESTED_BY} />
                <ATMDropdown
                  selection
                  multiple
                  clearable
                  search
                  options={filters.requestorNames?.map((requestor) => ({
                    key: requestor?.replace(' ', ''),
                    value: requestor,
                    text: requestor,
                  }))}
                  value={getValue('requestorName')}
                  onChange={(_, { value }) => setFilterValues(value, 'requestorName')}
                />
              </ATMGrid.Column>
            </ATMGrid.Row>
            <ATMGrid.Row className={style.filterRow}>
              <ATMGrid.Column>
                <span className={style.filterButtons}>
                  <ATMButton
                    secondary
                    content={Lang.LBL_CLEAR}
                    onClick={() => {
                      setFilterParams([]);
                      handleParams({
                        ...parameters,
                        filters: undefined,
                      });
                    }}
                  />
                  <ATMButton
                    primary
                    content={Lang.LBL_APPLY}
                    onClick={() => handleFetch()}
                  />
                </span>
              </ATMGrid.Column>
            </ATMGrid.Row>
          </ATMGrid>
        )}
      </ATMGrid>
    </>
  );
};

export default FlightsCalendarFilter;
